import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNoindex } from '@/hooks/useNoindex';

interface LocationState {
  from?: string;
}

export default function AdminLogin() {
  useNoindex();
  const { session, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from || '/admin/contacts';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (session) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('メールアドレスとパスワードを入力してください。');
      return;
    }
    setSubmitting(true);
    const { error: signInError } = await signIn(email.trim(), password);
    setSubmitting(false);
    if (signInError) {
      setError('ログインに失敗しました。メールアドレスとパスワードをご確認ください。');
      return;
    }
    navigate(from, { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-dashboard-3-line text-xl" />
          </span>
          <h1 className="font-heading text-xl font-semibold text-foreground-950">管理コンソール</h1>
          <p className="mt-1 text-sm text-foreground-500">ログインして管理画面を開きます</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-lg border border-background-200 bg-background-50 p-6"
        >
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-900">
              <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
                <i className="ri-error-warning-line" />
              </span>
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">
              メールアドレス
            </label>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-mail-line text-sm" />
              </span>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                className="w-full bg-transparent py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground-700">
              パスワード
            </label>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-lock-line text-sm" />
              </span>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-transparent py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
              >
                <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                ログイン中...
              </>
            ) : (
              <>
                <i className="ri-login-box-line" />
                ログイン
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}